import { useId, useState } from 'react';

import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';

import { Flex } from 'components/layout';
import { Typography } from 'components/typography';

import { CalculationStep } from '../types';

type KitReasoningProps = {
  summary?: string;
  steps: CalculationStep[];
};

// Auto transport reasoning from the backend: headline summary, then factors behind a toggle.
export function KitReasoning({ summary, steps }: KitReasoningProps) {
  const [showFactors, setShowFactors] = useState(false);
  const factorsId = useId();

  return (
    <Flex direction='col' gap='sm' className='w-full'>
      {summary && (
        <Typography
          data-testid='kit-insights-summary'
          variant='body-xs'
          textColor='lessMuted'
          className='leading-snug'
        >
          {summary}
        </Typography>
      )}

      {steps.length > 0 && (
        <button
          type='button'
          aria-expanded={showFactors}
          aria-controls={factorsId}
          className='w-fit text-brand hover:underline'
          onClick={(event) => {
            event.stopPropagation();
            setShowFactors((open) => !open);
          }}
          // The parent card handles Enter and Space and calls preventDefault,
          // which would stop this button from toggling.
          onKeyDown={(event) => event.stopPropagation()}
        >
          <Flex align='center' gap='xs'>
            <Typography
              as='span'
              variant='body-xxs'
              weight='medium'
              className='text-inherit'
            >
              {showFactors ? 'Hide full reasoning' : 'Show full reasoning'}
            </Typography>
            {showFactors ? (
              <ChevronUpIcon className='h-3 w-3' aria-hidden />
            ) : (
              <ChevronDownIcon className='h-3 w-3' aria-hidden />
            )}
          </Flex>
        </button>
      )}

      {showFactors && (
        <Flex
          id={factorsId}
          direction='col'
          gap='sm'
          data-testid='kit-insights-factors'
        >
          {steps.map((step, index) => (
            <Flex key={`${step.id}-${index}`} direction='col' gap='xs'>
              <Typography
                variant='body-xs'
                weight='medium'
                className='text-neutral-900'
              >
                {step.title}
              </Typography>
              <Typography
                variant='body-xxs'
                textColor='lessMuted'
                className='leading-snug'
              >
                {step.description}
              </Typography>
            </Flex>
          ))}
        </Flex>
      )}
    </Flex>
  );
}
